import { FC, RefObject, useCallback, useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import { Icon, IconButton } from '@revolut-internal/ui-kit'

import { Tooltip } from 'components/Tooltip/Tooltip'
import { useCreateAgentsRoute } from 'hooks/useCreateAgentsRoute'
import { Security } from 'modules/security/security'
import { ResourcesPanelList } from 'pages/Monitoring/components/ResourcesPanel/ResourcesPanelList'
import { ResourcesPanelSection } from 'pages/Monitoring/components/ResourcesPanel/ResourcesPanelSection'
import { ResourcesSideParams, SpecialisationHealth } from 'types'
import { pluralize } from 'utils/pluralize'

import { useAgents } from './useAgents'
import { useAgentsSorting } from './useAgentsSorting'

type QueueHealthAgentsProps = {
  resourcesSideParams: ResourcesSideParams
  scrollRef: RefObject<HTMLDivElement | null>
  queueHealthSpecialisations?: SpecialisationHealth[]
}

export const QueueHealthAgents: FC<QueueHealthAgentsProps> = ({
  resourcesSideParams,
  queueHealthSpecialisations,
  scrollRef,
}) => {
  const history = useHistory()

  const {
    agents = [],
    agentIds,
    isPending,
  } = useAgents(queueHealthSpecialisations, resourcesSideParams)
  const { sortedAgents, AgentsSorting, onSortingReset } = useAgentsSorting(agents)
  const createAgentsRoute = useCreateAgentsRoute(agentIds)

  useEffect(onSortingReset, [onSortingReset, resourcesSideParams.filterType])

  const keyExtractor = useCallback(
    (index: number) => sortedAgents[index].agent.id,
    [sortedAgents],
  )

  return (
    <ResourcesPanelSection
      title={pluralize('agent', sortedAgents.length)}
      action={
        Security.canViewAgentAttendance && (
          <Tooltip message={`Filter ${pluralize('agent', agentIds?.length, true)}`}>
            <IconButton
              useIcon="UsersSearch"
              color="grey-tone-50"
              useHoverIcon={<Icon name="UsersSearch" size={16} color="accent" />}
              size={16}
              onClick={() => history.push(createAgentsRoute())}
            >
              To agent dashboard
            </IconButton>
          </Tooltip>
        )
      }
      side={AgentsSorting}
      isPending={isPending}
      isEmpty={!agentIds?.length}
    >
      <ResourcesPanelList
        scrollRef={scrollRef}
        resources={sortedAgents}
        keyExtractor={keyExtractor}
        entityName="agent"
        isPending={isPending}
      />
    </ResourcesPanelSection>
  )
}
